import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Logger from '../../services/Logger';
import { useCommonConfiguration } from '../../hooks/useCommonConfiguration';
import { languageSelector } from '../../redux/reducers/app/params';
import { PRODUCT_NAMES_MAP } from '../../constants/productTypes';
import { NAVIGATION_LINK_TYPES } from '../../constants/navigation';

const logger = new Logger(`SeoLinkComponent`);

const getLinkUrlFromDeeplink = ({
  linkConfig,
  baseServicesUrls,
  baseCampaignTypeUrls,
  language,
  productType,
}) => {
  switch (linkConfig?.type) {
    case NAVIGATION_LINK_TYPES.DEEPLINK: {
      try {
        const url = new URL(linkConfig?.uriPath);
        const searchParams = url.searchParams;

        const campaign_type = searchParams.get('campaign_type');
        const campaign_param = searchParams.get('campaign_param');

        if (campaign_type === 'absolute' && campaign_param) {
          return campaign_param;
        }

        if (campaign_type === 'service' && campaign_param) {
          try {
            const linkParams = JSON.parse(campaign_param);
            const path = linkParams?.path;
            const serviceName = linkParams?.['service-name'];
            const serviceBaseUrl = baseServicesUrls[serviceName];

            return serviceBaseUrl ? `/${language}${serviceBaseUrl}${path}` : '';
          } catch (e) {
            logger.error('Error during json parsing');
            return '';
          }
        }

        if (campaign_type) {
          const campaignTypeBaseUrl = baseCampaignTypeUrls[campaign_type];
          return campaignTypeBaseUrl
            ? `/${language}${campaignTypeBaseUrl}`
            : '';
        }
      } catch (e) {
        logger.error('Error during setup of deeplink in SeoConfig', linkConfig);
        return '';
      }
      break;
    }

    case NAVIGATION_LINK_TYPES.SERVICE:
    case NAVIGATION_LINK_TYPES.INTERNAL: {
      if (!productType) return '';

      const serviceName = PRODUCT_NAMES_MAP[productType];
      const serviceBaseUrl = baseServicesUrls[serviceName];

      if (linkConfig?.uriPath && serviceBaseUrl) {
        return `/${language}${serviceBaseUrl}${linkConfig?.uriPath}`;
      }

      return serviceBaseUrl ? `/${language}${serviceBaseUrl}` : '';
    }

    default:
      return '';
  }
};

export const useGetSeoLinkUrl = ({ linkConfig, url, productType }) => {
  const commonConfig = useCommonConfiguration();
  const language = useSelector(languageSelector);

  const baseServicesUrls =
    useMemo(
      () =>
        commonConfig?.abaServicesBaseUrls?.servicesUrls?.reduce?.(
          (acc, curr) => {
            acc[curr.productType] = curr.url;

            return acc;
          },
          {}
        ),
      [commonConfig]
    ) ?? {};

  const baseCampaignTypeUrls =
    useMemo(
      () =>
        commonConfig?.abaServicesBaseUrls?.campaignTypeBaseUrls?.reduce?.(
          (acc, curr) => {
            acc[curr.campaignType] = curr.url;
            return acc;
          },
          {}
        ),
      [commonConfig]
    ) ?? {};

  if ((!url && !linkConfig) || !commonConfig?.abaServicesBaseUrls) return '';

  const targetProductType = linkConfig?.productType ?? productType;

  if (url && targetProductType) {
    const serviceBaseUrl = baseServicesUrls[targetProductType];

    return serviceBaseUrl ? `/${language}${serviceBaseUrl}${url}` : '';
  }

  if (url) {
    return url;
  }

  return getLinkUrlFromDeeplink({
    linkConfig,
    baseServicesUrls,
    baseCampaignTypeUrls,
    language,
    productType: targetProductType,
  });
};
